import { useLanguage } from '@/i18n/LanguageContext';

/** What the server sends for a product wherever the Seller Portal lists one (all optional: older rows carry only code/name). */
export type SkuDisplayFields = {
  readonly code: string | null;
  readonly name: string | null;
  readonly article?: string | undefined;
  readonly size?: string | undefined;
  readonly color?: string | undefined;
  /** Canonical barcode first (the server orders them deterministically). */
  readonly barcodes?: readonly string[] | undefined;
  /** Legacy single barcode, used only when `barcodes` is absent. */
  readonly barcode?: string | null | undefined;
};

/** The optional display fields of a server row, copied only when present (the types forbid explicit undefined). */
export function skuDisplay(l: { readonly article?: string | undefined; readonly size?: string | undefined; readonly color?: string | undefined; readonly barcodes?: readonly string[] | undefined }): {
  article?: string; size?: string; color?: string; barcodes?: readonly string[];
} {
  return {
    ...(l.article ? { article: l.article } : {}),
    ...(l.size ? { size: l.size } : {}),
    ...(l.color ? { color: l.color } : {}),
    ...(l.barcodes ? { barcodes: l.barcodes } : {}),
  };
}

/** At most this many barcodes are spelled out; the rest read as "+N". */
export const MAX_SHOWN_BARCODES = 3;

/** Article / size / colour and the barcode(s) of a product, as small secondary text. Renders nothing when there is none. */
export function SkuMeta({ sku }: { sku: SkuDisplayFields }): JSX.Element | null {
  const { t } = useLanguage();
  const attrs: string[] = [];
  if (sku.article && sku.article !== sku.code) attrs.push(`${t('sku.article')} ${sku.article}`);
  if (sku.size) attrs.push(`${t('sku.size')} ${sku.size}`);
  if (sku.color) attrs.push(`${t('sku.color')} ${sku.color}`);
  const all = sku.barcodes ?? (sku.barcode ? [sku.barcode] : []);
  const shown = all.slice(0, MAX_SHOWN_BARCODES);
  const more = all.length - shown.length;
  if (attrs.length === 0 && shown.length === 0) return null;
  return (
    <>
      {attrs.length > 0 && <div className="meta sku-attrs">{attrs.join(' · ')}</div>}
      {shown.length > 0 && (
        <div className="meta sku-barcodes">
          {all.length > 1 ? t('sku.barcodes') : t('sku.barcode')}: {shown.join(', ')}
          {more > 0 && ` +${more}`}
        </div>
      )}
    </>
  );
}

/** "CODE — Name" with the product's article/size/colour/barcode underneath. */
export function SkuInfo({ sku }: { sku: SkuDisplayFields }): JSX.Element {
  return (
    <>
      <strong>{sku.code ?? '—'}</strong> — {sku.name ?? ''}
      <SkuMeta sku={sku} />
    </>
  );
}
